import { ShieldCheck, ServerOff, KeyRound, UserX, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow, SectionHeading } from "@/components/Reveal";
import { useDemoForm } from "@/components/DemoForm";

const POINTS = [
  {
    icon: ServerOff,
    title: "No platform, no data",
    desc: "VeraChart does not host a software app and does not ingest, process, or store Protected Health Information (PHI).",
  },
  {
    icon: KeyRound,
    title: "Your environment, your control",
    desc: "We provide the prompt architecture. You run it inside your agency's secure, BAA-covered AI environment.",
  },
  {
    icon: UserX,
    title: "De-identification enforced",
    desc: "Our training requires the use of de-identified placeholders (e.g., \u201cClient,\u201d \u201cClient's Mother\u201d) to support the agency's privacy and compliance requirements.",
  },
];

export function Security() {
  const { openDialog } = useDemoForm();
  return (
    <section id="security" className="bg-white py-20 lg:py-28" aria-labelledby="security-heading">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl bg-navy p-8 sm:p-12 lg:p-16" data-testid="security-panel">
          <div className="grid items-start gap-12 lg:grid-cols-5">
            <Reveal className="lg:col-span-2">
              <Eyebrow dark>Security &amp; Privacy</Eyebrow>
              <SectionHeading id="security-heading" dark>Zero PHI. 100% Agency Control.</SectionHeading>
              <p className="mt-6 text-base leading-relaxed text-slate-300 sm:text-lg">
                VeraChart is designed to minimize vendor-side data exposure: the prompts and training are ours — the
                agency maintains control of its data environment.
              </p>
              <button
                type="button"
                data-testid="security-ask-button"
                onClick={() => openDialog("security", "We'd like to discuss VeraChart's security model and our agency's AI platform.")}
                className="mt-8 inline-flex items-center gap-2 rounded-full border border-teal-500 px-8 py-3.5 font-display text-base font-semibold text-teal-300 transition-colors hover:bg-teal-600 hover:text-white"
              >
                Ask Us About Security &amp; Deployment <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </button>
            </Reveal>

            <div className="space-y-5 lg:col-span-3">
              {POINTS.map((p, i) => (
                <Reveal key={p.title} delay={0.08 + i * 0.08}>
                  <div className="flex items-start gap-5 rounded-2xl border border-navy-700 bg-navy-800 p-6">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-600/15 text-teal-400">
                      <p.icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="font-display text-base font-bold text-white">{p.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-slate-300">{p.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
              <Reveal delay={0.3}>
                <p className="flex items-center gap-2 pl-1 text-xs text-slate-400">
                  <ShieldCheck className="h-4 w-4 text-teal-400" aria-hidden="true" />
                  Because VeraChart never ingests, processes, or stores PHI, your clinical data environment stays
                  entirely under your agency's control.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
